import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-link.js';
import '@adornis/chemistry/elements/components/x-loader.js';
import '@adornis/chemistry/elements/components/x-section.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import type { brandDict } from '@ils/translation/translation.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { filter, switchMap } from 'rxjs';
import { ExamDay, type Round } from '../../db/examdays.js';
import { type ILSUser } from '../../db/ils-user.js';
import { ServerTime } from '../../db/time.js';

/**
 * @element ils-tester-dashboard
 */
@customElement('ils-tester-dashboard')
export class IlsTesterDashboard extends ChemistryLitElement {
  @state() private readonly branding = new TranslationController<brandDict>(this);
  @state() private readonly _serverTime = new RXController(this, ServerTime.subscribeServerTime()());

  @state() private readonly _currentUser = new RXController(this, currentUser<ILSUser>().pipe(filter(Boolean)));
  @state() private readonly _bookedExamDays = new RXController(
    this,
    this._currentUser.observable.pipe(
      filter(Boolean),
      switchMap(user => {
        return ExamDay.getFutureAndBookedExamDays(user._id)({
          start: 1,
          end: 1,
          osceTesterTuples: { osce: { durationInMinutes: 1, taskType: 1 }, testerIDs: 1 },
          blockPause: 1,
          breakInMinutes: 1,
          breakAfterRounds: 1,
          slotPause: 1,
          fallbackTesters: 1,
        });
      }),
    ),
  );

  @state() private _stepLength = 4;

  override render() {
    if (this._currentUser.isLoading)
      return html`
        <x-flex w100 h100 center crossaxis-center>
          <x-loader></x-loader>
        </x-flex>
      `;
    if (!this._currentUser.value)
      return html`<x-empty-placeholder .text=${this.branding.translate('error', 'no_access')}></x-empty-placeholder>`;

    return html`
      <x-section ${css({ padding: this.spacing.md })}>
        <x-flex space="xl">
          <x-flex horizontal wrap space="lg">
            ${this._nextAppointments(this._currentUser.value, this._bookedExamDays.value)}
          </x-flex>
        </x-flex>
      </x-section>
    `;
  }

  private _nextAppointments(currentUser: ILSUser, bookedAppointments: ExamDay[] = []) {
    return html`
      <x-flex space="md" flex ${css({ minWidth: '300px' })}>
        <x-flex horizontal space="md" space-between crossaxis-end>
          <x-h3> Nächste Termine </x-h3>
        </x-flex>
        ${this._examDayList(currentUser, bookedAppointments)}
      </x-flex>
    `;
  }

  private _examDayList(currentUser: ILSUser, bookedAppointments: ExamDay[] = []) {
    if (!bookedAppointments.length)
      return html`
        <x-flex crossaxis-center space="sm">
          <x-empty-placeholder .text=${'Noch keine Termine gebucht'} .img=${'close'}></x-empty-placeholder>
          <x-button icon="east" href="/start/tester-book-appointments"> Jetzt zur Termin-Buchung </x-button>
        </x-flex>
      `;

    return html`
      <x-flex space="sm">
        ${repeat(bookedAppointments.slice(0, this._stepLength), (appointment, i) => {
          const isAppointmentRunning = this._isAppointmentRunning(appointment);
          const rounds = appointment.getBookedRoundsForTester(currentUser);
          const card = html`
            <x-basic-card
              w100
              padding="md"
              space="sm"
              horizontal
              crossaxis-center
              href=${ifDefined(isAppointmentRunning ? '#' : undefined)}
              ${css({ cursor: isAppointmentRunning ? 'pointer' : 'initial' })}
            >
              <x-flex space="md">
                <x-h3> Prüfung am ${appointment.start.toLocaleString(DateTime.DATE_SHORT)} </x-h3>
                <x-flex space="md" flex> ${this._roundList(rounds)} </x-flex>
              </x-flex>
              ${isAppointmentRunning ? html` <x-button mode="icon" icon="event"> </x-button> ` : nothing}
            </x-basic-card>
          `;
          return isAppointmentRunning ? html` <x-link href="/start/scheduling"> ${card} </x-link> ` : card;
        })}
      </x-flex>
      ${bookedAppointments.length - this._stepLength > 0
        ? html`
            <x-button
              mode="text"
              href="#"
              ${css({ alignSelf: 'center' })}
              @click=${(e: CustomEvent) => {
                e.stopPropagation();
                this._stepLength += 4;
              }}
            >
              ${bookedAppointments.length - this._stepLength} Weitere Termine...
            </x-button>
          `
        : nothing}
      <x-button icon="event" href="/start/tester-book-appointments"> Zu weiteren Prüfungstagen anmelden </x-button>
    `;
  }

  private _roundList(rounds: Round[]) {
    return html`
      <x-flex space="sm">
        ${repeat(rounds, round => {
          const start = DateTime.fromMillis(round.timeline.start);
          const end = DateTime.fromMillis(round.timeline.end);
          return html`
            <x-flex space="xs">
              <x-text bold>
                ${round.round + 1}. Runde (${start.toLocaleString(DateTime.TIME_24_SIMPLE)} Uhr - ca.
                ${end.toLocaleString(DateTime.TIME_24_SIMPLE)} Uhr)
              </x-text>
              <x-text tone="subtle"> ${round.fallbackTesterID ? 'Ersatzschauspieler' : 'Schauspieler'} </x-text>
            </x-flex>
          `;
        })}
      </x-flex>
    `;
  }

  private _isAppointmentRunning(appointment: ExamDay) {
    const currentTime = this._serverTime.value ? DateTime.fromJSDate(this._serverTime.value) : null;
    if (!currentTime) return false;
    return appointment.start <= currentTime.plus({ minutes: 30 }) && appointment.end > currentTime;
  }
}
